import axios from 'axios';
import { getCurrentAuthToken } from 'Utils/Auth';
import type { AudioRecCalibrationProfile } from '../types';
import { sanitizeCalibrationProfile } from '../calibration/profile';

type StoredUserAudioRecProfile = {
  profile?: unknown;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const baseUrl = backendUrl ? `${backendUrl}/api/v1/user-audio-rec-profiles` : null;

const getAuthHeaders = async () => {
  if (!baseUrl) return null;
  const token = await getCurrentAuthToken();
  if (!token) return null;
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const fetchActiveCalibrationProfileFromApi =
  async (): Promise<AudioRecCalibrationProfile | null> => {
    const headers = await getAuthHeaders();
    if (!headers || !baseUrl) return null;

    try {
      const response = await axios.get<StoredUserAudioRecProfile | null>(`${baseUrl}/active`, {
        headers,
      });
      const rawProfile = response.data?.profile;
      return sanitizeCalibrationProfile(rawProfile ?? null);
    } catch {
      return null;
    }
  };

export const saveCalibrationProfileToApi = async (
  profile: AudioRecCalibrationProfile,
): Promise<void> => {
  const headers = await getAuthHeaders();
  if (!headers || !baseUrl) return;

  try {
    await axios.post(
      baseUrl,
      {
        label: profile.label,
        profile,
      },
      { headers },
    );
  } catch (error) {
    console.warn('[AudioRecognition] Failed to save calibration profile to API.', error);
  }
};

export const clearActiveCalibrationProfileFromApi = async (): Promise<void> => {
  const headers = await getAuthHeaders();
  if (!headers || !baseUrl) return;

  try {
    await axios.delete(`${baseUrl}/active`, { headers });
  } catch (error) {
    console.warn('[AudioRecognition] Failed to clear active calibration profile from API.', error);
  }
};
